// The browser's client of the cosmos API (ADR 0002): the same Bearer token as the web API.
import { type CosmosClient, createCosmosClient } from "@cosmosai/api-client";
import { useMemo } from "react";
import { useRuntimeConfig } from "../app/config-context";
import { useAuth } from "../features/auth/auth-context";
import type { AuthPort } from "../features/auth/auth-port";
import { reauthenticate } from "./unauthorized";

/** A cosmos client that sends the access token and signs in again on a 401. */
export function createBrowserCosmosClient(
  auth: Pick<AuthPort, "accessToken" | "signIn">,
  baseUrl: string,
): CosmosClient {
  const client = createCosmosClient({ baseUrl, token: () => auth.accessToken() });
  // onResponse runs in reverse order: this sees the 401 before the problem middleware throws.
  client.use({
    async onResponse({ request, response }) {
      if (response.status === 401 && request.headers.has("authorization")) {
        await reauthenticate(auth);
      }
      return response;
    },
  });
  return client;
}

/** The cosmos API client of this page, built once per AuthPort; "" calls the same origin. */
export function useCosmos(): CosmosClient {
  const auth = useAuth();
  const { cosmosApiUrl } = useRuntimeConfig();
  return useMemo(() => createBrowserCosmosClient(auth, cosmosApiUrl), [auth, cosmosApiUrl]);
}
